import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { academyApi } from '../../services/academyApi';
import type { AcademyQuestion } from '@platform/shared-types';
import type { Enqueue } from './CurriculumTab';

/** How long typing must pause before the question bank autosaves. */
const AUTOSAVE_MS = 800;

const blank = (chapterId: string): AcademyQuestion => ({
  questionId: crypto.randomUUID(),
  chapterId,
  prompt: '',
  choices: [
    { id: 'a', text: '' }, { id: 'b', text: '' },
    { id: 'c', text: '' }, { id: 'd', text: '' },
  ],
  correctChoiceId: 'a',
  explanation: '',
});

export default function QuestionEditor(
  { chapterId, enqueue }: { chapterId: string; enqueue: Enqueue },
) {
  const [questions, setQuestions] = useState<AcademyQuestion[]>([]);
  const [error, setError] = useState('');

  const questionsRef = useRef<AcademyQuestion[]>([]);
  const dirtyRef = useRef(false);
  questionsRef.current = questions;

  useEffect(() => {
    void academyApi.listQuestions(chapterId)
      .then((r) => setQuestions(r.questions))
      .catch((e) => setError(e.message));
  }, [chapterId]);

  const flush = () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    // The backend rejects with 400 unless `questions` is an array — an empty
    // array (every prompt still blank) is fine, `undefined` is not.
    void enqueue(() => academyApi.saveQuestions(
      chapterId,
      questionsRef.current.filter((q) => q.prompt.trim()),
    ))
      .then(() => setError(''))
      .catch((e: any) => setError(e.message || 'Those questions did not save.'));
  };

  /** Autosave — nothing is disabled and nothing reloads, so edits can't be dropped. */
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(flush, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [questions]);

  /** Flush on unmount — the parent remounts this on every chapter switch. */
  useEffect(() => flush, []);

  const edit = (next: AcademyQuestion[]) => {
    dirtyRef.current = true;
    setQuestions(next);
  };

  const patch = (i: number, p: Partial<AcademyQuestion>) =>
    edit(questions.map((q, qi) => (qi === i ? { ...q, ...p } : q)));

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200">
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">
          Questions for this chapter ({questions.length})
        </h3>
        <button
          type="button"
          onClick={() => edit([...questions, blank(chapterId)])}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-700 transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Question
        </button>
      </div>

      <div className="p-5 space-y-3">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
        {questions.length === 0 && (
          <p className="text-sm text-gray-500">
            No questions yet. Write a few that only someone who watched the video can answer.
          </p>
        )}
        {questions.map((q, i) => (
          <div key={q.questionId} className="border border-gray-200 rounded-xl bg-white p-4">
            <input
              value={q.prompt}
              placeholder="Ask something a rep will actually face"
              onChange={(e) => patch(i, { prompt: e.target.value })}
              onBlur={flush}
              className="w-full min-h-[44px] bg-transparent text-gray-900 font-semibold border-b border-gray-300 focus:border-gray-900 outline-none"
            />
            <div className="mt-3 space-y-2">
              {q.choices.map((c, ci) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 px-3 min-h-[44px] rounded-lg border cursor-pointer transition ${
                    q.correctChoiceId === c.id
                      ? 'border-gray-900 bg-white ring-1 ring-gray-900'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name={`correct-${q.questionId}`}
                    checked={q.correctChoiceId === c.id}
                    onChange={() => patch(i, { correctChoiceId: c.id })}
                    className="accent-gray-900 w-4 h-4"
                  />
                  <input
                    value={c.text}
                    placeholder={`Answer ${String.fromCharCode(65 + ci)}`}
                    onChange={(e) => patch(i, {
                      choices: q.choices.map((y, yi) => (yi === ci ? { ...y, text: e.target.value } : y)),
                    })}
                    onBlur={flush}
                    className="flex-1 bg-transparent text-sm text-gray-900 outline-none"
                  />
                </label>
              ))}
            </div>
            <input
              value={q.explanation || ''}
              placeholder="Why (shown after a wrong answer)"
              onChange={(e) => patch(i, { explanation: e.target.value })}
              onBlur={flush}
              className="mt-3 w-full min-h-[44px] bg-white border border-gray-300 rounded-lg px-3 text-sm text-gray-600 outline-none focus:border-gray-900"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
